import { Fragment, useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useTenantVertical from "../../hooks/useTenantVertical";
import { makeVT } from "../../utils/verticals";
import { Bell, ChevronDown, LogOut, Menu, Search, UserRound, Zap } from "lucide-react";
import useManagerStore from "../../store/managerStore";
import { useAuthStore } from "../../store/authStore";
import { useLangPrefix } from "../../hooks/useLangPrefix";
import LanguageSwitcher from "./LanguageSwitcher";
import { canAccess, normalizeRole, TENANT_FEATURE_FLAGS } from "../../lib/rbac";
import { NAV_GROUPS, isNavItemNew, markNavItemSeen, resolveNavGroups } from "./navConfig";
import { useFeatureFlag } from "../../lib/useFeatureFlag";
import { billingView } from "../../lib/billingStatus";
import BillingStatusBanners from "./BillingStatusBanners";
import AnnouncementBanner from "./AnnouncementBanner";
import UserAvatar from "../common/UserAvatar";
import { useBellNotifications } from "../../hooks/useBellNotifications";


function NavItem({ label, icon, active, collapsed, badge, onClick }) {
  const Icon = icon;
  return (
    <button
      onClick={onClick}
      className={`relative w-full flex items-center gap-3 py-2 rounded-lg text-sm transition-colors ${
        collapsed ? "justify-center px-0" : "px-3"
      } ${
        active
          ? "bg-primary-soft text-primary font-medium"
          : "text-text-muted hover:bg-zinc-50 hover:text-text-main"
      }`}
    >
      {active && !collapsed && (
        <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-primary rounded-r" />
      )}
      <Icon size={18} strokeWidth={1.9} />
      {!collapsed && <span className="text-sm">{label}</span>}
      {!collapsed && badge && (
        <span className="ml-auto rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white">
          {badge}
        </span>
      )}
    </button>
  );
}

function ExpandableNav({
  label,
  icon,
  active,
  collapsed,
  expanded,
  onToggle,
  onCollapsedClick,
  children,
}) {
  const Icon = icon;
  return (
    <div>
      <button
        type="button"
        onClick={collapsed ? onCollapsedClick : onToggle}
        className={`relative flex items-center w-full gap-3 py-2 rounded-lg text-sm transition-colors ${
          collapsed ? "justify-center px-0" : "px-3"
        } ${
          active
            ? "bg-primary-soft text-primary font-medium"
            : "text-text-muted hover:bg-zinc-50 hover:text-text-main"
        }`}
      >
        {active && !collapsed && (
          <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-primary rounded-r" />
        )}
        <Icon size={18} strokeWidth={1.9} />
        {!collapsed && <span className="flex-1 text-left text-sm">{label}</span>}
        {!collapsed && (
          <ChevronDown
            size={16}
            className={`text-zinc-400 transition-transform duration-200 ${
              expanded ? "rotate-0" : "-rotate-90"
            }`}
          />
        )}
      </button>
      {!collapsed && expanded && <div className="mt-1 ml-8 space-y-0.5">{children}</div>}
    </div>
  );
}

function SubNav({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`block w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
        active
          ? "bg-primary-soft text-primary font-medium"
          : "text-text-muted hover:bg-zinc-50 hover:text-primary"
      }`}
    >
      {label}
    </button>
  );
}

const CREDIT_BAR_COLOR = { ok: "#1535c8", warning: "#f59e0b", danger: "#ef4444" };

export default function DesktopShell({ children }) {
  const { t } = useTranslation();
  const tenantVertical = useTenantVertical();
  const vt = makeVT(t, tenantVertical);
  const navigate = useNavigate();
  const location = useLocation();
  const { prefix } = useLangPrefix();
  const { tenantId, tenantName, creditStatus, role: storeRole } = useManagerStore();
  const branches = useAuthStore((s) => s.branches);
  const selectBranch = useAuthStore((s) => s.selectBranch);
  const authUser = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const role = normalizeRole(storeRole);
  const tagsFeatureEnabled = useFeatureFlag(TENANT_FEATURE_FLAGS.contact_tags, false);
  const customStatusFeatureEnabled = useFeatureFlag(TENANT_FEATURE_FLAGS.custom_status, false);
  const ordersFeatureEnabled = useFeatureFlag(TENANT_FEATURE_FLAGS.orders, false);
  const bookingsFeatureEnabled = useFeatureFlag(TENANT_FEATURE_FLAGS.bookings, false);
  const faqFeatureEnabled = useFeatureFlag(TENANT_FEATURE_FLAGS.faq, false);
  const botLogsFeatureEnabled = useFeatureFlag(TENANT_FEATURE_FLAGS.bot_logs, false);
  const playbookFeatureEnabled = useFeatureFlag(TENANT_FEATURE_FLAGS.playbook, false);
  const quickAnswerFeatureEnabled = useFeatureFlag(TENANT_FEATURE_FLAGS.quick_answer, false);
  const featureFlagValues = {
    [TENANT_FEATURE_FLAGS.contact_tags]: tagsFeatureEnabled,
    [TENANT_FEATURE_FLAGS.custom_status]: customStatusFeatureEnabled,
    [TENANT_FEATURE_FLAGS.orders]: ordersFeatureEnabled,
    [TENANT_FEATURE_FLAGS.bookings]: bookingsFeatureEnabled,
    [TENANT_FEATURE_FLAGS.faq]: faqFeatureEnabled,
    [TENANT_FEATURE_FLAGS.bot_logs]: botLogsFeatureEnabled,
    [TENANT_FEATURE_FLAGS.playbook]: playbookFeatureEnabled,
    [TENANT_FEATURE_FLAGS.quick_answer]: quickAnswerFeatureEnabled,
  };
  const navGroups = resolveNavGroups(NAV_GROUPS, {
    canAccessModule: (moduleName) => canAccess(role, moduleName),
    isFlagOn: (flagKey) => Boolean(featureFlagValues[flagKey]),
  });

  const [collapsed, setCollapsed] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [branchPickerOpen, setBranchPickerOpen] = useState(false);
  const [branchSearch, setBranchSearch] = useState("");
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);
  const {
    notifications: displayedNotifications,
    unreadCount: displayedUnreadCount,
    refresh: refreshNotifications,
    markAllRead: handleMarkAllRead,
    remove: handleDeleteNotif,
    open: openBellNotification,
  } = useBellNotifications();

  const openNotification = (notification) => {
    setNotifOpen(false);
    openBellNotification(notification);
  };

  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  function isActive(path) {
    const full = `${prefix}${path}`;
    return location.pathname === full || location.pathname.startsWith(`${full}/`);
  }

  function go(path) {
    navigate(`${prefix}${path}`);
  }

  // Opening an item (click or direct URL) retires its NEW badge in this browser.
  // Only top-level items are scanned: badges are not supported on expandable
  // children. Hidden items are skipped so a URL visit before the item is
  // available (or by a user without access) does not consume the badge.
  useEffect(() => {
    for (const item of NAV_GROUPS.flatMap((group) => group.items)) {
      if (!item.newUntil || !item.available) continue;
      if (item.module && !canAccess(role, item.module)) continue;
      const full = `${prefix}${item.route}`;
      if (location.pathname === full || location.pathname.startsWith(`${full}/`)) {
        markNavItemSeen(item.key);
      }
    }
  }, [location.pathname, prefix, role]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const sidebarWidth = collapsed ? 64 : 248;
  const billing = billingView(creditStatus);
  const credUsed = billing?.credits.used ?? 0;
  const credTotal = billing?.credits.total ?? 0;
  const credPct = billing?.credits.usedPct ?? 0;
  const barColor = CREDIT_BAR_COLOR[billing?.tone ?? "ok"];

  const fallbackDisplayName = t("common.userFallback");
  const rawDisplayName = authUser?.username ?? authUser?.email ?? tenantName ?? fallbackDisplayName;
  const displayName =
    typeof rawDisplayName === "string"
      ? rawDisplayName
      : String(rawDisplayName ?? fallbackDisplayName);
  const activeBranch =
    branches.find((branch) => branch?.tenant_id && branch.tenant_id === tenantId) ??
    branches[0] ??
    null;
  const normalizedSearch = branchSearch.trim().toLowerCase();
  const filteredBranches = !normalizedSearch
    ? branches
    : branches.filter((branch) => {
        const name = (branch?.name || "").toLowerCase();
        const address = (branch?.address || "").toLowerCase();
        return name.includes(normalizedSearch) || address.includes(normalizedSearch);
      });

  function renderNavEntry(item) {
    if (!item.children) {
      const active = isActive(item.route);
      return (
        <NavItem
          key={item.key}
          label={t(item.labelKey)}
          icon={item.icon}
          active={active}
          collapsed={collapsed}
          badge={!active && isNavItemNew(item) ? t("nav.badgeNew") : null}
          onClick={() => go(item.route)}
        />
      );
    }
    return (
      <ExpandableNav
        key={item.key}
        label={t(item.labelKey)}
        icon={item.icon}
        active={item.children.some((child) => isActive(child.route))}
        collapsed={collapsed}
        expanded={openMenu === item.key || item.children.some((child) => isActive(child.route))}
        onToggle={() => setOpenMenu((current) => (current === item.key ? null : item.key))}
        onCollapsedClick={() => go(item.children[0].route)}
      >
        {item.children.map((child) => (
          <SubNav
            key={child.key}
            label={child.verticalLabel ? vt(child.labelKey) : t(child.labelKey)}
            active={isActive(child.route)}
            onClick={() => go(child.route)}
          />
        ))}
      </ExpandableNav>
    );
  }

  return (
    <div className="min-h-screen bg-page-bg text-text-main">
      <aside
        className="fixed top-0 left-0 bottom-0 bg-white border-r border-border z-40 flex flex-col overflow-hidden"
        style={{ width: sidebarWidth, transition: "width 0.2s ease" }}
      >
        <div
          className="h-16 flex items-center flex-shrink-0 border-b border-border"
          style={{
            paddingLeft: collapsed ? 0 : 20,
            justifyContent: collapsed ? "center" : "flex-start",
          }}
        >
          {collapsed ? (
            <img src="/short_logo.png" alt="Growchat" className="w-10 h-auto rounded-md object-cover" />
          ) : (
            <img src="/full_logo.png" alt="Growchat" className="w-32 h-auto rounded-md object-cover" />
          )}
        </div>

        <div className="flex-1 overflow-y-auto py-3 space-y-0.5">
          {!collapsed && branches.length > 0 && (
            <div className="px-3 mb-3">
              <button
                type="button"
                onClick={() => {
                  setBranchSearch("");
                  setBranchPickerOpen(true);
                }}
                className="w-full flex items-center justify-between gap-2 rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-100"
              >
                <span className="inline-flex items-center gap-2 min-w-0">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-zinc-200 text-zinc-600">
                    <Zap size={14} />
                  </span>
                  <span className="truncate font-medium">
                    {activeBranch?.name || t("branchGate.switchBranch")}
                  </span>
                </span>
                <ChevronDown size={16} className="text-zinc-500" />
              </button>
            </div>
          )}

          {navGroups.map((group, index) => (
            <Fragment key={group.key}>
              {!collapsed ? (
                <p
                  className={`px-3 text-xs text-zinc-400 font-semibold uppercase tracking-wider mb-2${
                    index > 0 ? " mt-8" : ""
                  }`}
                >
                  {t(group.labelKey)}
                </p>
              ) : (
                index > 0 && <div className="my-3 mx-3 border-t border-border" />
              )}
              {group.items.map(renderNavEntry)}
            </Fragment>
          ))}
        </div>

        {creditStatus && (
          <div
            className={`flex-shrink-0 border-t border-border ${collapsed ? "py-3 flex justify-center" : "px-4 py-3"}`}
          >
            {collapsed ? (
              <div className="w-2 h-10 bg-zinc-100 rounded-full overflow-hidden flex flex-col justify-end">
                <div style={{ height: `${credPct}%`, background: barColor }} className="w-full rounded-full transition-all" />
              </div>
            ) : (
              <>
                <div className="flex justify-between text-xs text-zinc-500 mb-1.5">
                  <span className="font-medium text-[13px]">{t("shell.aiCredits")}</span>
                  <span>
                    {credUsed} / {credTotal}
                  </span>
                </div>
                <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${credPct}%`, background: barColor }} />
                </div>
                <p className="text-[11px] text-zinc-400 mt-1">{t("shell.percentUsed", { pct: credPct })}</p>
              </>
            )}
          </div>
        )}
      </aside>

      <header
        className="fixed top-0 right-0 h-16 bg-white border-b border-border z-30 flex items-center px-4 justify-between"
        style={{ left: sidebarWidth, transition: "left 0.2s ease" }}
      >
        <button onClick={() => setCollapsed((c) => !c)} className="w-8 h-8 flex items-center justify-center text-zinc-500 hover:bg-zinc-100 rounded-lg">
          <Menu size={18} />
        </button>

        <div className="flex items-center gap-3">
          <div className="shrink-0">
            <LanguageSwitcher />
          </div>

          <div ref={notifRef} className="relative">
            <button
              onClick={() =>
                setNotifOpen((o) => {
                  const next = !o;
                  if (next) refreshNotifications("bell-open");
                  return next;
                })
              }
              className="relative w-8 h-8 flex items-center justify-center text-zinc-500 hover:bg-zinc-100 rounded-lg"
            >
              <Bell size={18} />
              {displayedUnreadCount > 0 && (
                <span className="absolute top-1 right-1 min-w-[12px] h-[12px] px-0.5 bg-orange-400 rounded-full text-white text-[9px] font-bold flex items-center justify-center">
                  {displayedUnreadCount > 9 ? "9+" : displayedUnreadCount}
                </span>
              )}
            </button>

            {notifOpen && (
              <div className="absolute right-0 top-10 w-80 bg-white border border-zinc-200 rounded-xl shadow-lg z-50 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-100">
                  <span className="text-sm font-semibold text-zinc-700">{t("notifications.title")}</span>
                  {displayedUnreadCount > 0 && (
                    <button onClick={handleMarkAllRead} className="text-xs text-primary hover:underline">
                      {t("notifications.markAllRead")}
                    </button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto divide-y divide-zinc-50">
                  {displayedNotifications.length === 0 ? (
                    <p className="text-sm text-zinc-400 text-center py-6">{t("notifications.empty")}</p>
                  ) : (
                    displayedNotifications.map((n) => (
                      <div
                        key={n.id}
                        onClick={() => openNotification(n)}
                        className={`relative px-4 py-3 cursor-pointer hover:bg-zinc-50 transition-colors ${!n.read ? "bg-orange-50" : ""}`}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteNotif(n.id);
                          }}
                          className="absolute top-2 right-3 text-zinc-300 hover:text-zinc-500 text-sm leading-none"
                          title={t("notifications.delete")}
                        >
                          ✕
                        </button>
                        <p className={`text-sm font-medium pr-4 ${!n.read ? "text-zinc-800" : "text-zinc-600"}`}>{n.title}</p>
                        <p className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{n.body}</p>
                      </div>
                    ))
                  )}
                </div>
                <button
                  onClick={() => {
                    setNotifOpen(false);
                    navigate(`${prefix}/notifications`);
                  }}
                  className="block w-full border-t border-zinc-100 px-4 py-2.5 text-center text-xs font-medium text-primary hover:bg-zinc-50"
                >
                  {t("notifications.viewAll")} →
                </button>
              </div>
            )}
          </div>

          <div ref={userMenuRef} className="relative">
            <button
              onClick={() => setUserMenuOpen((open) => !open)}
              aria-label={displayName}
              title={displayName}
              className="flex items-center rounded-full p-0.5 hover:bg-zinc-100 transition-colors"
            >
              <UserAvatar url={authUser?.avatar_url} name={authUser?.name ?? displayName} size={36} />
            </button>

            {userMenuOpen && (
              <div className="absolute top-full right-0 mt-1.5 bg-white border border-zinc-200 rounded-xl shadow-md min-w-40 z-50 overflow-hidden">
                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    go("/settings");
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-zinc-700 hover:bg-zinc-50 flex items-center gap-2"
                >
                  <UserRound size={15} />
                  {t("nav.settings")}
                </button>
                <div className="h-px bg-zinc-100" />
                <button
                  onClick={async () => {
                    setUserMenuOpen(false);
                    await logout();
                    navigate(`${prefix}/login`);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 flex items-center gap-2"
                >
                  <LogOut size={15} />
                  {t("settingsPage.logOut")}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main
        className="min-h-screen bg-page-bg px-6"
        style={{
          marginLeft: sidebarWidth,
          paddingTop: 80,
          transition: "margin-left 0.2s ease",
        }}
      >
        <BillingStatusBanners className="mb-3" />
        <AnnouncementBanner className="mb-3" />
        {children}
      </main>

      {branchPickerOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/30 flex items-start justify-center pt-24 px-4"
          onClick={() => setBranchPickerOpen(false)}
        >
          <div
            className="w-full max-w-3xl rounded-2xl border border-zinc-200 bg-white shadow-xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                type="text"
                value={branchSearch}
                onChange={(e) => setBranchSearch(e.target.value)}
                placeholder={t("branchGate.searchPlaceholder")}
                className="w-full rounded-xl border border-zinc-300 pl-11 pr-3 py-3 text-base outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>

            <p className="mt-6 mb-3 text-xs font-semibold uppercase tracking-wider text-zinc-400">
              {t("branchGate.allAccounts")}
            </p>

            <div className="max-h-[55vh] overflow-auto space-y-3 pr-1">
              {filteredBranches.map((branch) => {
                const disabled = !branch?.tenant_id;
                const isActive = !!tenantId && branch?.tenant_id === tenantId;
                const branchInitial = (branch?.name || "?").trim().charAt(0).toUpperCase();

                return (
                  <button
                    key={branch.id}
                    type="button"
                    disabled={disabled}
                    onClick={async () => {
                      if (disabled) return;
                      const result = await selectBranch(branch.id);
                      if (result?.ok) setBranchPickerOpen(false);
                    }}
                    className={`w-full rounded-xl border px-4 py-3 text-left transition-colors ${
                      disabled
                        ? "cursor-not-allowed border-zinc-100 bg-zinc-50 text-zinc-300"
                        : isActive
                          ? "border-primary bg-primary-soft/40"
                          : "border-zinc-200 bg-white hover:bg-zinc-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-zinc-600 font-semibold">
                        {branchInitial}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-lg font-semibold text-zinc-900">
                          {branch?.name || "-"}
                        </span>
                        <span className="block truncate text-sm text-zinc-400">
                          {branch?.address || t("branchGate.noAddress")}
                        </span>
                      </span>
                    </div>
                  </button>
                );
              })}

              {filteredBranches.length === 0 && (
                <div className="rounded-xl border border-dashed border-zinc-200 px-4 py-6 text-center text-sm text-zinc-500">
                  {t("branchGate.noMatchingBranches")}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
